import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  GetTenantCommodityMappingDetails,
  GetTenantCommodityMappings,
  UpdateTenantCommodityMapping,
} from 'src/api/microservices/platform-markets-commodity-config'
import type {
  MappingDetails,
  MappingListItem,
  UpdateCommodityMappingRequest,
} from 'src/api/microservices/platform-markets-commodity-config'
import { useCompanyContext } from 'src/companies/routes/company-context'

export type {
  MappingDetails,
  MappingListItem,
  MappingLocation,
  MappingStatus,
  TenantCommodity,
  UpdateCommodityMapping,
} from 'src/api/microservices/platform-markets-commodity-config'

export const PLATFORM_COMMODITY_MAPPINGS_QUERY_KEY = 'platform-commodity-mappings'
export const PLATFORM_COMMODITY_MAPPING_DETAILS_QUERY_KEY = 'platform-commodity-mapping-details'
export const PLATFORM_COMMODITY_UPDATE_MAPPING_MUTATION_KEY = 'platform-commodity-update-mapping'

export function useGetTenantCommodityMappings() {
  const {
    company: { tenant_id: tenantId, datasource_id: datasourceId },
  } = useCompanyContext()

  return useQuery<MappingListItem[]>({
    queryKey: [PLATFORM_COMMODITY_MAPPINGS_QUERY_KEY, { tenantId, datasourceId }],
    queryFn: async () => {
      if (!tenantId || !datasourceId) throw new Error('A tenant and datasource are required.')
      const [err, response] = await GetTenantCommodityMappings({ datasourceId }, tenantId)
      if (err) throw err
      return response.mappings
    },
    enabled: !!tenantId && !!datasourceId,
  })
}

export function useGetTenantCommodityMappingDetails(commodityId: string) {
  const {
    company: { tenant_id: tenantId, datasource_id: datasourceId },
  } = useCompanyContext()

  return useQuery<MappingDetails>({
    queryKey: [
      PLATFORM_COMMODITY_MAPPING_DETAILS_QUERY_KEY,
      { tenantId, datasourceId, commodityId },
    ],
    queryFn: async () => {
      if (!tenantId || !datasourceId || !commodityId) {
        throw new Error('A tenant, datasource and commodity are required.')
      }
      const [err, response] = await GetTenantCommodityMappingDetails({ commodityId }, tenantId)
      if (err) throw err
      return response.mapping
    },
    enabled: !!tenantId && !!datasourceId && !!commodityId,
    retry: false,
    gcTime: 0,
  })
}

export function useUpdateTenantCommodityMapping() {
  const {
    company: { tenant_id: tenantId, datasource_id: datasourceId },
  } = useCompanyContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [PLATFORM_COMMODITY_UPDATE_MAPPING_MUTATION_KEY, { tenantId, datasourceId }],
    mutationFn: async (data: UpdateCommodityMappingRequest) => {
      if (!tenantId || !datasourceId) throw new Error('A tenant and datasource are required.')
      const [err] = await UpdateTenantCommodityMapping(data, tenantId)
      if (err) throw err

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [PLATFORM_COMMODITY_MAPPINGS_QUERY_KEY, { tenantId, datasourceId }],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            PLATFORM_COMMODITY_MAPPING_DETAILS_QUERY_KEY,
            { tenantId, datasourceId, commodityId: data.commodityId },
          ],
        }),
      ])
    },
  })
}
